import React, { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Navbar from '../components/homepage/Navbar';
import HeroSection from '../components/homepage/HeroSection';
import Contact from '../components/homepage/Contact';
import Footer from '../components/homepage/Footer';
import { Box, Typography, useMediaQuery, useTheme, InputAdornment, TextField } from '@mui/material';
import { getImageFromStorage, getAppointmentById } from '../firebase/firebase-delegate';
import ServicesSection from '../components/employee/services/ServicesSection';
import { Dialog, DialogContent, Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Appointment, BookingConfirmation, Service } from '../types';
import { getDownloadURL, ref } from 'firebase/storage';
import { storage } from '../firebase/firebase';
import LoadingSpinner from '../components/global/LoadingSpinner';
import { motion } from 'framer-motion';
import GallerySection from './Admin/Gallery/GalleryHomepageSection';
import BusinessHoursCard from '../components/homepage/BusinessHoursCard';
import UnderConstructionPage from './UnderConstructionPage';
import { ConfirmationNumberOutlined } from '@mui/icons-material';
import { ScrollFadeIn } from '../components/homepage/utils/Utils';
import StaffSection, { EmployeeWithPhoto } from '../components/homepage/StaffSection';
import ReviewsSection from '../components/homepage/ReviewsSection';
import { getCachedAssetUrl } from '../utils/assetCache';
import { useLocation } from 'react-router-dom';
import { isSubscriptionAccessible } from '../utils/subscriptionAccess';
import { resolveBookingUxConfig } from '../utils/bookingUxConfig';
import { useTenant } from '../tenancy/TenantContext';
import {
  fetchPublicEmployees,
  fetchPublicServices,
  getHomepageRuntimeConfig,
  getTenantPublicOrigin,
  HomepageRuntimeConfig,
} from '../sitePublishing/publicConfigSource';
import {
  RecentBookingSnapshot,
  buildAppointmentFromSnapshot,
  buildRecentBookingSnapshot,
  clearRecentBookingSnapshot,
  isWithinOneHourOfBooking,
  loadRecentBookingSnapshot,
  saveRecentBookingSnapshot,
} from '../utils/recentBookingUtils';
import { requestEmbeddedBookingClose } from '../booking/embeddedBookingBridge';

// Lazy-load the heavy booking dialog bundle only when the user opens it.
const BookNowDialog = lazy(() => import('../components/booking/BookNowDialog'));

const Homepage: React.FC = () => {
  const { tenantId } = useTenant();
  const location = useLocation();
  const [openBooking, setOpenBooking] = useState(false); // Modal state for Booking
  const [logoUrl, setLogoUrl] = useState<string | null>(null); // Logo image URL
  const [heroLogoUrl, setHeroLogoUrl] = useState<string | null>(null);

  const [heroImageURL, setHeroImageURL] = useState<string | null>(null);
  const [assetsReady, setAssetsReady] = useState(false);
  const [appointment, setAppointment] = useState<Appointment | null>(null);
  const [recentBookingData, setRecentBookingData] = useState<RecentBookingSnapshot | null>(null);
  const [bookingStartStep, setBookingStartStep] = useState<number | null>(null);

  useEffect(() => {
    if (location.pathname !== '/' || window.location.port !== '3000' || !window.location.search.includes('tenant=')) return;
    void getTenantPublicOrigin(tenantId).then((origin) => {
      const destination = new URL('/', origin);
      const current = new URL(window.location.href);
      current.searchParams.delete('tenant');
      current.searchParams.forEach((value, key) => destination.searchParams.append(key, value));
      destination.hash = current.hash;
      window.location.replace(destination.toString());
    }).catch(() => undefined);
  }, [location.pathname, tenantId]);

  const [appointmentIdInput, setAppointmentIdInput] = useState('');
  const [appointmentIdError, setAppointmentIdError] = useState<string | null>(null);

  const handleGoToAppointment = async () => {
    const id = appointmentIdInput.trim();
    if (!id) {
      setAppointmentIdError('Please enter your appointment ID.');
      return;
    }
    try {
      await getAppointmentById(id, tenantId);
    } catch {
      setAppointmentIdError('Sorry, we could not find that appointment.');
      return;
    }
    // Navigate relative to current site
    window.location.assign(`/myappointment?id=${encodeURIComponent(id)}`);
  };

  const servicesQuery = useQuery<Service[]>({
    queryKey: ['publicServices', tenantId],
    queryFn: () => fetchPublicServices(tenantId),
  });

  const employeesQuery = useQuery<EmployeeWithPhoto[]>({
    queryKey: ['publicEmployeesWithPhotos', tenantId],
    queryFn: async () => {
      const employees = await fetchPublicEmployees(tenantId);
      return Promise.all(
        employees.map(async (employee) => {
          const picturePath = employee.profilePictureImagePath;
          if (!picturePath) {
            return employee;
          }

          try {
            const url = await getCachedAssetUrl(
              picturePath,
              () => getImageFromStorage(picturePath)
            );
            if (!url) {
              return employee;
            }
            return { ...employee, profilePictureUrl: url };
          } catch (error) {
            console.error('Failed to load employee profile picture', error);
            return employee;
          }
        })
      );
    },
  });

  const publicBookableEmployeeIds = useMemo(() => {
    const ids = new Set<string>();
    (servicesQuery.data ?? []).forEach((service) => {
      (service.employees ?? []).forEach((employeeId) => {
        if (employeeId) {
          ids.add(employeeId);
        }
      });
    });
    return ids;
  }, [servicesQuery.data]);
  const publicStaff = useMemo(
    () => (employeesQuery.data ?? []).filter(
      (employee) => employee.active !== false && publicBookableEmployeeIds.has(employee.id)
    ),
    [employeesQuery.data, publicBookableEmployeeIds],
  );
  const showServicesLink = (servicesQuery.data ?? []).length > 0;
  const isBookNowRoute = location.pathname === '/book-now';
  const isEmbeddedBookNow = isBookNowRoute && new URLSearchParams(location.search).get('embed') === '1';
  const isStandaloneBookNow = isEmbeddedBookNow && new URLSearchParams(location.search).get('standalone') === '1';
  const isRebookRoute = isBookNowRoute && new URLSearchParams(location.search).get('rebook') === '1';
  const preferredEmployeeId = (() => {
    const employeeId = new URLSearchParams(location.search).get('employee')?.trim();
    return employeeId || undefined;
  })();

  const businessConfigQuery = useQuery<HomepageRuntimeConfig | null>({
    queryKey: ['homepageRuntimeConfig', tenantId],
    queryFn: () => getHomepageRuntimeConfig(tenantId),
  });

  const config = businessConfigQuery.data;
  const bookingEnabled = config?.publicSiteRuntime?.bookingEnabled ?? true;
  const bookingCtaLabel = config?.publicSiteRuntime?.bookingCtaLabel ?? 'Book Now';
  const bookingUxConfig = useMemo(
    () => resolveBookingUxConfig(businessConfigQuery.data),
    [businessConfigQuery.data]
  );
  useEffect(() => {
    if (!businessConfigQuery.data) return;

    let isCancelled = false;

    const preloadImage = (src: string) =>
      new Promise<void>((resolve, reject) => {
        const img = new Image();
        img.src = src;
        img.onload = () => resolve();
        img.onerror = () =>
          reject(new Error('Failed to preload image.'));
      });

    // Wrap getDownloadURL so we reuse signed URLs and let the browser HTTP cache handle bytes.
    const fetchAsset = async (path?: string | null) => {
      if (!path) {
        return null;
      }
      try {
        const cachedUrl = await getCachedAssetUrl(path, async () => {
          const downloadUrl = await getDownloadURL(ref(storage, path));
          return downloadUrl;
        });

        if (cachedUrl) {
          await preloadImage(cachedUrl);
        }

        return cachedUrl;
      } catch (error) {
        console.error('Failed to load asset:', error);
        return null;
      }
    };

    const loadImages = async () => {
      const heroImagePath = businessConfigQuery.data?.homepage?.heroImagePath;
      const logoImagePath = businessConfigQuery.data?.businessInfo?.logoImagePath;
      const heroLogoImagePath = businessConfigQuery.data?.homepage?.heroLogoImagePath;

      // Fire all downloads in parallel and block render until each asset is ready.
      const [heroImage, navbarLogo, heroLogo] = await Promise.all([
        fetchAsset(heroImagePath),
        fetchAsset(logoImagePath),
        fetchAsset(heroLogoImagePath),
      ]);

      if (!isCancelled) {
        setHeroImageURL(heroImage);
        setLogoUrl(navbarLogo);
        setHeroLogoUrl(heroLogo);
      }
    };

    setAssetsReady(false);
    loadImages()
      .catch((error) => {
        console.error('Failed to load homepage assets:', error);
      })
      .finally(() => {
        if (!isCancelled) {
          setAssetsReady(true);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [businessConfigQuery.data]);

  const handleAppointment = (appointment: Appointment | null) => {
    setAppointment(appointment);
  };

  useEffect(() => {
    setRecentBookingData(loadRecentBookingSnapshot(tenantId));
  }, [tenantId]);

  useEffect(() => {
    if (!isBookNowRoute) {
      return;
    }
    if (!servicesQuery.isSuccess || !employeesQuery.isSuccess || !businessConfigQuery.isSuccess) {
      return;
    }

    if (isRebookRoute && recentBookingData) {
      setAppointment(buildAppointmentFromSnapshot(recentBookingData, servicesQuery.data ?? []));
      setBookingStartStep(2);
    } else {
      setAppointment(null);
      setBookingStartStep(null);
    }
    setOpenBooking(true);
  }, [
    isBookNowRoute,
    preferredEmployeeId,
    isRebookRoute,
    recentBookingData,
    servicesQuery.isSuccess,
    employeesQuery.isSuccess,
    businessConfigQuery.isSuccess,
  ]);

  const handleBookingSuccess = (booking: BookingConfirmation) => {
    const snapshot = buildRecentBookingSnapshot(booking, tenantId);
    saveRecentBookingSnapshot(snapshot);
    setRecentBookingData(snapshot);
  };

  const handleForgetRecentBooking = () => {
    clearRecentBookingSnapshot(tenantId);
    setRecentBookingData(null);
  };

  const isLoading =
    servicesQuery.isLoading ||
    servicesQuery.isFetching ||
    businessConfigQuery.isLoading ||
    employeesQuery.isLoading ||
    businessConfigQuery.isFetching ||
    (Boolean(businessConfigQuery.data) && !assetsReady);
  const hasQueryError = servicesQuery.isError || businessConfigQuery.isError || employeesQuery.isError;

  const theme = useTheme();
  // Determine if the screen is mobile, if yes, then show booking dialog in fullscreen
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleOpenBooking = () => {
    setOpenBooking(true);
    setAppointment(null);
    setBookingStartStep(null);
  };
  const handleBookAgain = () => {
    if (!recentBookingData) return;
    const draftAppointment = buildAppointmentFromSnapshot(recentBookingData, servicesQuery.data ?? []);
    setAppointment(draftAppointment);
    setBookingStartStep(2);
    setOpenBooking(true);
  };
  const handleCloseBooking = () => {
    if (isEmbeddedBookNow) {
      if (!requestEmbeddedBookingClose(tenantId)) {
        window.location.assign('/');
      }
      return;
    }
    setOpenBooking(false); // Close modal
    setAppointment(null);
    setBookingStartStep(null);
  };
  const shouldShowRebookPrompt =
    Boolean(recentBookingData) && !isWithinOneHourOfBooking(recentBookingData);

  if (isLoading) return <LoadingSpinner />;

  // A failed public data read is an operational error, not an unpublished site.
  // Keep it distinct from the intentional Under Construction state below.
  if (hasQueryError) {
    return <Typography color="error">Booking is temporarily unavailable. Please try again.</Typography>;
  }

  const paymentInfo = config?.paymentInfo;
  const hasSubscriptionAccess = config?.publicSiteRuntime?.source === 'published' ||
    isSubscriptionAccessible(paymentInfo);
  const logoPlacement = config?.homepage?.logoPlacement ?? 'navbar';
  const navbarLogoUrl = (logoPlacement === 'navbar' || logoPlacement === 'both') ? logoUrl : null;
  const heroOverlayLogoUrl = (logoPlacement === 'heroOverlay' || logoPlacement === 'both') ? heroLogoUrl : null;
  const aboutUsContent = config?.homepage?.aboutUs?.trim();
  const showHomepageGallery =
    bookingUxConfig.uiFlags.showHomepageGallerySection &&
    (businessConfigQuery.data?.homepage?.gallery ?? []).length > 0;
  const showHomepageTeam = bookingUxConfig.uiFlags.showHomepageTeamSection;
  const showHomepageReviews = bookingUxConfig.uiFlags.showHomepageReviewsSection;
  const showHomepageAbout = bookingUxConfig.uiFlags.showHomepageAboutSection && Boolean(aboutUsContent);
  const homepageResourcePluralLabel = bookingUxConfig.terminology.resourcePlural;

  if (
    !config ||
    !config.businessInfo?.name ||
    !config.homepage?.heroTitle ||
    !hasSubscriptionAccess
  ) {
    return <UnderConstructionPage />;
  }

  if (isEmbeddedBookNow && !bookingEnabled) {
    return (
      <Box
        role="status"
        sx={{
          minHeight: '100vh',
          px: 3,
          py: 6,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          textAlign: 'center',
          backgroundColor: 'background.default',
        }}
      >
        <Typography variant="h5" fontWeight={700}>
          Online booking is currently unavailable
        </Typography>
        <Typography color="text.secondary">
          {isStandaloneBookNow
            ? 'Please check back later or contact the business directly.'
            : 'Please return to the website for contact details and business hours.'}
        </Typography>
        {!isStandaloneBookNow && (
          <Button variant="contained" color="primary" onClick={handleCloseBooking}>
            Return to website
          </Button>
        )}
      </Box>
    );
  }

  return (
    <>
      {!isEmbeddedBookNow && (
        <>
      <Navbar
        onOpenBooking={handleOpenBooking}
        bookingEnabled={bookingEnabled}
        bookingCtaLabel={bookingCtaLabel}
        logoUrl={navbarLogoUrl}
        businessInfo={businessConfigQuery.data!.businessInfo}
        logoPlacement={logoPlacement}
        showServicesLink={showServicesLink}
      />

      {/* Hero Section with fade-in */}
      <div id="hero">
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <HeroSection
            onOpenBooking={handleOpenBooking}
            bookingEnabled={bookingEnabled}
            bookingCtaLabel={bookingCtaLabel}
            onBookAgain={handleBookAgain}
            onForgetRecentBooking={handleForgetRecentBooking}
            showBookAgain={shouldShowRebookPrompt}
            homepageInfo={businessConfigQuery.data!.homepage}
            heroImageURL={heroImageURL}
            logoPlacement={logoPlacement}
            logoUrl={heroOverlayLogoUrl}
            businessName={businessConfigQuery.data!.businessInfo.name}
          />
        </motion.div>
      </div>

      {/* Services Section with scroll-in animation */}
      <div id="services">
        <ScrollFadeIn>
          <ServicesSection services={servicesQuery.data ?? []} />
        </ScrollFadeIn>
      </div>

      {showHomepageGallery && (
        <Box id="gallery" sx={{ mt: { xs: 2, md: 2 } }}>
          <ScrollFadeIn>
            <GallerySection gallery={businessConfigQuery.data?.homepage?.gallery ?? []} />
          </ScrollFadeIn>
        </Box>
      )}


      {/* About Us Section */}
      {showHomepageTeam && (
        <StaffSection
          employees={publicStaff}
          showBio={bookingUxConfig.uiFlags.showEmployeeBio}
          showSocials={bookingUxConfig.uiFlags.showEmployeeSocials}
          showProfilePhoto={bookingUxConfig.uiFlags.showEmployeeProfilePhoto}
          resourcePluralLabel={homepageResourcePluralLabel}
        />
      )}

      {showHomepageReviews && <ReviewsSection reviews={businessConfigQuery.data?.homepage?.reviews} />}

      {showHomepageAbout && (
        <Box id="about-us" px={2} mt={{ xs: 2, md: 2 }} mb={{ xs: 2, md: 2 }} style={{ backgroundColor: theme.palette.background.default }}>
          <ScrollFadeIn>
            <Box maxWidth="md" mx="auto" textAlign="center">
              <Typography variant="h4" gutterBottom fontWeight={600}>
                About Us
              </Typography>
              <Typography variant="body1" color="textSecondary" sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
                {aboutUsContent}
              </Typography>
            </Box>
          </ScrollFadeIn>
        </Box>
      )}

      {/* Business Hours Section */}
      <Box id="hours" px={2} mt={{ xs: 2, md: 2 }} mb={{ xs: 2, md: 2 }} sx={{ backgroundColor: theme.palette.background.default }}>
        <ScrollFadeIn>
          <Box maxWidth="md" mx="auto" textAlign="center">
            <BusinessHoursCard hours={businessConfigQuery.data!.businessInfo.businessHours} />
          </Box>
        </ScrollFadeIn>
      </Box>

      <Box id="manage-appointment" px={2} mt={{ xs: 2, md: 2 }} mb={{ xs: 2, md: 2 }} sx={{ backgroundColor: theme.palette.background.default }}>
        <Box maxWidth="md" mx="auto" textAlign="center">
          {/* Animate once on first scroll into view; never again */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          >
            <Typography variant="h4" gutterBottom fontWeight={600}>
              Find My Appointment
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ mb: 2 }}
            >
              Enter your Appointment ID to view, reschedule, or cancel.
            </Typography>
          </motion.div>

          <Box maxWidth={600} mx="auto">
            <Box
              component="form"
              onSubmit={(e) => {
                e.preventDefault();
                handleGoToAppointment();
              }}
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'stretch',
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              <TextField
                fullWidth
                variant="filled"
                label="Appointment ID"
                placeholder="e.g. ABC1234"
                value={appointmentIdInput}
                onChange={(e) => {
                  setAppointmentIdInput(e.target.value);
                  setAppointmentIdError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleGoToAppointment();
                }}
                error={!!appointmentIdError}
                helperText={appointmentIdError ?? ' '}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <ConfirmationNumberOutlined />
                      </InputAdornment>
                    ),
                  },
                }}
                sx={{
                  '& .MuiFilledInput-root': {
                    borderRadius: '20px',
                    backgroundColor: (t) =>
                      t.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'white',
                  },
                }}
              />

              <Button
                variant="contained"
                color="primary"
                onClick={handleGoToAppointment}
                sx={{
                  px: 3,
                  height: { xs: 48, sm: 56 },
                  borderRadius: '20px',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  color: 'common.white',
                  '&:hover': {
                    color: 'common.white',
                  },
                }}
              >
                Search
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>


      {/* Contact + Footer */}
      < div id="contact" >
        <Contact businessConfig={businessConfigQuery.data!} />
      </div >
      <Footer />
        </>
      )}

      {/* Booking Modal */}
      {bookingEnabled && openBooking && (
        <Dialog
          open
          onClose={(event, reason) => {
            if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
              handleCloseBooking();
            }
          }}
          fullWidth
          maxWidth="sm"
          fullScreen={isMobile || isEmbeddedBookNow}
          sx={{
            height: isMobile || isEmbeddedBookNow ? '100%' : '95%',
            '& .MuiDialog-paper': {
              height: isMobile || isEmbeddedBookNow ? '100%' : '95%',
              maxHeight: 'none',
              margin: isMobile || isEmbeddedBookNow ? 0 : 'auto',
            },
          }}
        >
          {!isEmbeddedBookNow && (
          <IconButton
            onClick={handleCloseBooking}
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              color: '#000',
              zIndex: 1,
            }}
          >
            <CloseIcon />
          </IconButton>
          )}
          <DialogContent sx={{ padding: '0 20px', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Suspense fallback={<LoadingSpinner />}>
              <BookNowDialog appointment={appointment}
                handleAppointment={handleAppointment}
                services={servicesQuery.data ?? []}
                handleCloseBooking={handleCloseBooking}
                enabledCustomerSMS={businessConfigQuery.data?.features.enableCustomerSMS ?? false}
                enabledEmployeeSMS={businessConfigQuery.data?.features.enableEmployeeSMS ?? false}
                enabledPayments={businessConfigQuery.data?.features.enablePayments ?? false}
                requirePaymentForBooking={businessConfigQuery.data?.preferences?.requirePaymentForBooking ?? false}
                minimumBookingNoticeHours={businessConfigQuery.data?.preferences?.minimumBookingNoticeHours ?? 1}
                onBookingSuccess={handleBookingSuccess}
                prefillCustomer={recentBookingData ? {
                  firstName: recentBookingData.customerFirstName,
                  lastName: recentBookingData.customerLastName,
                  email: recentBookingData.customerEmail,
                  phone: recentBookingData.customerPhoneNumber,
                } : null}
                preferredEmployeeId={
                  bookingUxConfig.bookingModel === 'resource'
                    ? undefined
                    : preferredEmployeeId
                }
                initialStep={bookingStartStep ?? undefined}
                showPrices={config?.publicSiteRuntime?.showPrices ?? true}
                showEmployeePicker={config?.publicSiteRuntime?.source === 'published'
                  ? config.publicSiteRuntime.showEmployeePicker
                  : undefined}
                showCloseAction={!isStandaloneBookNow}
              />
            </Suspense>
          </DialogContent>
        </Dialog>
      )}
    </>

  );
};

export default Homepage;
